import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';

export default function AvisoTradutor() {
  const [visivel, setVisivel] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    const isTranslated = () =>
      document.documentElement.classList.contains('translated-ltr') ||
      document.documentElement.classList.contains('translated-rtl');

    const atualizar = () => {
      if (isTranslated()) {
        if (!timerRef.current) {
          timerRef.current = setTimeout(() => {
            setVisivel(true);
            timerRef.current = null;
          }, 1200);
        }
      } else {
        clearTimeout(timerRef.current);
        timerRef.current = null;
        setVisivel(false);
      }
    };

    atualizar();

    const observer = new MutationObserver(atualizar);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    });

    return () => {
      observer.disconnect();
      clearTimeout(timerRef.current);
    };
  }, []);

  const handleFechar = () => setVisivel(false);

  if (!visivel) return null;

  return (
    <CartaoAviso translate="no" className="notranslate">
      <BotaoFechar onClick={handleFechar} title="Fechar">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </BotaoFechar>

      <CabecalhoAviso>
        <IconeAviso>⚠️</IconeAviso>
        <TituloAviso>Tradução automática detectada</TituloAviso>
      </CabecalhoAviso>

      <CorpoAviso>
        <p>
          O <strong>Google Tradutor</strong> do Chrome está traduzindo automaticamente
          esta página, alterando nomes, datas e campos — podendo causar{' '}
          <strong>erros</strong>.
        </p>

        <BarraChrome>
          <BarraLabel>Procure este ícone na barra do Chrome:</BarraLabel>
          <BarraSimulada>
            <BarraUrl>
              <BarraUrlTexto>tpedf.com.br/home</BarraUrlTexto>
              <IconeDestaque title="Ícone Google Translate">
                <IconeTradutor />
              </IconeDestaque>
            </BarraUrl>
            <BarraDireita>
              <IconeGenerico>☆</IconeGenerico>
              <IconeGenerico>⋮</IconeGenerico>
            </BarraDireita>
          </BarraSimulada>
          <SetaCima>▲ clique aqui</SetaCima>
        </BarraChrome>

        <PassosContainer>
          <PassoTitulo>Como desativar:</PassoTitulo>

          <Passo>
            <PassoNumero>1</PassoNumero>
            <PassoTexto>
              Clique no <strong>ícone de tradução</strong> destacado em amarelo
              na barra de endereço do Chrome (canto superior direito).
            </PassoTexto>
          </Passo>

          <Passo>
            <PassoNumero>2</PassoNumero>
            <PassoTexto>
              No balão que abrir, clique nos <TresPontos>⋮</TresPontos>{' '}
              <strong>(três pontos)</strong> no canto direito do balão.
            </PassoTexto>
          </Passo>

          <Passo>
            <PassoNumero>3</PassoNumero>
            <PassoTexto>
              Selecione <OpcaoDestaque>"Nunca traduzir este site"</OpcaoDestaque>.
            </PassoTexto>
          </Passo>

          <Passo>
            <PassoNumero>4</PassoNumero>
            <PassoTexto>
              A página recarregará com o conteúdo{' '}
              <strong>original em português correto</strong>.
            </PassoTexto>
          </Passo>
        </PassosContainer>

          <NotaJaDesativou>
            💡 Após desativar a tradução, esta janela fechará automaticamente.
          </NotaJaDesativou>
      </CorpoAviso>

      <RodapeAviso>
        <BotaoEntendido onClick={handleFechar}>Entendi</BotaoEntendido>
      </RodapeAviso>
    </CartaoAviso>
  );
}

const IconeTradutor = () => (
  <svg width="22" height="22" viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg">
    {/* Balão direito (branco c/ borda preta) com símbolo 文 — ATRÁS */}
    <path d="M26 18 Q26 14 30 14 H54 Q58 14 58 18 V48 Q58 52 54 52 H42 L42 60 L36 52 H30 Q26 52 26 48 Z" fill="white" stroke="#000" strokeWidth="2.5"/>
    {/* Símbolo de tradução (文字 estilizado) */}
    <line x1="34" y1="26" x2="50" y2="26" stroke="#000" strokeWidth="2.5" strokeLinecap="round"/>
    <line x1="42" y1="22" x2="42" y2="26" stroke="#000" strokeWidth="2.5" strokeLinecap="round"/>
    <path d="M36 26 Q38 34 42 37 Q46 34 48 26" stroke="#000" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
    <line x1="37" y1="30" x2="47" y2="30" stroke="#000" strokeWidth="2" strokeLinecap="round"/>
    <line x1="42" y1="37" x2="38" y2="46" stroke="#000" strokeWidth="2.5" strokeLinecap="round"/>
    <line x1="42" y1="37" x2="46" y2="46" stroke="#000" strokeWidth="2.5" strokeLinecap="round"/>
    <line x1="37" y1="43" x2="47" y2="43" stroke="#000" strokeWidth="2" strokeLinecap="round"/>
    {/* Balão esquerdo (preto) com "G" — À FRENTE */}
    <path d="M6 8 Q6 4 10 4 H34 Q38 4 38 8 V38 Q38 42 34 42 H28 L22 50 L22 42 H10 Q6 42 6 38 Z" fill="#000"/>
    <text x="10" y="32" fontFamily="Arial Black,Arial,sans-serif" fontWeight="900" fontSize="22" fill="white">G</text>
  </svg>
);

const fadeSlideDown = keyframes`
  from { opacity: 0; transform: translateY(-10px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const pulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(245, 158, 11, 0.6); }
  50%       { box-shadow: 0 0 0 5px rgba(245, 158, 11, 0); }
`;

const CartaoAviso = styled.div`
  position: fixed;
  top: 72px;
  right: 16px;
  width: 320px;
  background: #fff;
  border-radius: 14px;
  border: 2px solid #F59E0B;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  z-index: 9000;
  animation: ${fadeSlideDown} 0.35s ease-out;
  font-family: 'Roboto', sans-serif;
  overflow: hidden;

  @media (max-width: 480px) {
    width: calc(100vw - 32px);
    right: 16px;
    top: 62px;
  }
`;

const CabecalhoAviso = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  background: linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%);
  padding: 12px 40px 12px 16px;
  border-bottom: 1px solid #FCD34D;
`;

const IconeAviso = styled.span`
  font-size: 20px;
  line-height: 1;
  flex-shrink: 0;
`;

const TituloAviso = styled.h3`
  margin: 0;
  font-size: 14px;
  font-weight: 700;
  color: #92400E;
  line-height: 1.3;
`;

const BotaoFechar = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  background: none;
  border: none;
  cursor: pointer;
  color: #92400E;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 3px;
  border-radius: 4px;
  transition: background 0.2s;
  &:hover { background: rgba(0,0,0,0.08); }
  svg { width: 14px; height: 14px; }
`;

const CorpoAviso = styled.div`
  padding: 14px 16px 8px;

  p {
    margin: 0 0 12px;
    font-size: 13px;
    color: #374151;
    line-height: 1.55;
  }

  strong { color: #B45309; }
`;

const BarraChrome = styled.div`
  margin-bottom: 14px;
`;

const BarraLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: #6B7280;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin-bottom: 5px;
`;

const BarraSimulada = styled.div`
  background: #3c4043;
  border-radius: 6px;
  padding: 5px 8px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const BarraUrl = styled.div`
  background: #5f6368;
  border-radius: 4px;
  padding: 3px 6px 3px 8px;
  font-size: 11px;
  color: #e8eaed;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  overflow: hidden;
`;

const BarraUrlTexto = styled.span`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
`;

const BarraDireita = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const IconeDestaque = styled.div`
  background: #fff;
  border-radius: 4px;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${pulse} 1.5s ease-in-out infinite;
  outline: 2.5px solid #F59E0B;
  cursor: default;
  flex-shrink: 0;
`;

const IconeGenerico = styled.span`
  color: #9aa0a6;
  font-size: 14px;
  line-height: 1;
`;

const SetaCima = styled.div`
  text-align: right;
  font-size: 11px;
  font-weight: 700;
  color: #F59E0B;
  margin-top: 3px;
`;

const PassosContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 10px;
`;

const NotaJaDesativou = styled.div`
  background: #F0FDF4;
  border: 1px solid #BBF7D0;
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 12px;
  color: #166534;
  line-height: 1.5;
  margin-bottom: 6px;

  em {
    font-style: normal;
    font-weight: 700;
  }
`;

const PassoTitulo = styled.div`
  font-size: 11px;
  font-weight: 700;
  color: #6B7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 2px;
`;

const Passo = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
`;

const PassoNumero = styled.span`
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #F59E0B;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 1px;
`;

const PassoTexto = styled.span`
  font-size: 12.5px;
  color: #374151;
  line-height: 1.5;

  strong { color: #1F2937; }
`;

const TresPontos = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #1f1f1f;
  color: #fff;
  font-size: 11px;
  font-weight: 900;
  padding: 1px 5px;
  border-radius: 3px;
  margin: 0 1px;
  vertical-align: middle;
  letter-spacing: 1px;
`;

const OpcaoDestaque = styled.span`
  background: #FEF3C7;
  border: 1px solid #FCD34D;
  border-radius: 3px;
  padding: 1px 5px;
  color: #92400E;
  font-weight: 600;
`;

const RodapeAviso = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 10px 16px 14px;
  border-top: 1px solid #F3F4F6;
`;

const BotaoEntendido = styled.button`
  background: #F59E0B;
  color: #fff;
  border: none;
  padding: 7px 18px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.2s;
  white-space: nowrap;

  &:hover { background: #D97706; }
`;
